// src/components/teams/RecruitingModal.jsx
// Minimal modal shell that reads Zustand state and loads your recruiting list on open.
// No styling changes elsewhere. We'll wire a button to open this in the next step (Z4).
import React, { useEffect, useState, useRef } from 'react';
import { useModalStore } from '../../state/modalStore';
import { useRecruitingStore } from '../../state/recruitingStore';
import useTeamStore from '../../state/teamStore';
import useOtherUsersStore from '../../state/otherUsersStore';
import ReactDOM from 'react-dom';
import { useMatrixStore } from '../../state/matrixStore';
import { useMessagingStore } from '../../state/messagingStore';
import { RiStickyNoteAddFill } from 'react-icons/ri';


// Build a Mapbox static map URL showing two service-area circles
function buildServiceOverlapMapUrl(overlap) {
  if (!overlap) return null;
  const viewerLat = Number(overlap.viewer_lat);
  const viewerLng = Number(overlap.viewer_lng);
  const candidateLat = Number(overlap.candidate_lat);
  const candidateLng = Number(overlap.candidate_lng);
  if (!Number.isFinite(viewerLat) || !Number.isFinite(viewerLng) ||
      !Number.isFinite(candidateLat) || !Number.isFinite(candidateLng)) return null;
  const viewerRadiusMiles = Number(overlap.viewer_radius_miles);
  const candidateRadiusMiles = Number(overlap.candidate_radius_miles);
  if (!Number.isFinite(viewerRadiusMiles) || !Number.isFinite(candidateRadiusMiles)) return null;
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token) return null;
  const milesToMeters = (m) => m * 1609.344;
  function circlePolygon(lng, lat, radiusMeters, steps = 64) {
    const coords = [];
    const earthRadius = 6378137;
    const latRad = (lat * Math.PI) / 180;
    const lngRad = (lng * Math.PI) / 180;
    const angular = radiusMeters / earthRadius;
    for (let i = 0; i <= steps; i++) {
      const bearing = (i / steps) * 2 * Math.PI;
      const sinLat = Math.sin(latRad); const cosLat = Math.cos(latRad);
      const sinAng = Math.sin(angular); const cosAng = Math.cos(angular);
      const lat2 = Math.asin(sinLat * cosAng + cosLat * sinAng * Math.cos(bearing));
      const lng2 = lngRad + Math.atan2(Math.sin(bearing) * sinAng * cosLat, cosAng - sinLat * Math.sin(lat2));
      coords.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
    }
    return coords;
  }
  const geojson = {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', properties: { 'fill': '#00aef7', 'fill-opacity': 0.18, 'stroke': '#00aef7', 'stroke-width': 2, 'stroke-opacity': 0.9 },
        geometry: { type: 'Polygon', coordinates: [circlePolygon(viewerLng, viewerLat, milesToMeters(viewerRadiusMiles))] } },
      { type: 'Feature', properties: { 'fill': '#f5c542', 'fill-opacity': 0.18, 'stroke': '#f5c542', 'stroke-width': 2, 'stroke-opacity': 0.9 },
        geometry: { type: 'Polygon', coordinates: [circlePolygon(candidateLng, candidateLat, milesToMeters(candidateRadiusMiles))] } }
    ]
  };
  const encoded = encodeURIComponent(JSON.stringify(geojson));
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/geojson(${encoded})/auto/300x200?access_token=${token}`;
}

const getPresenceBorder = (presence) => {
  if (presence === 'online') return 'border-2 border-green-500';
  if (presence === 'unavailable') return 'border-2 border-yellow-500';
  return 'border border-[#00aef7]/30';
};

const getStatusFill = (band) => {
  if (band === 'GREEN') return '#16a34a';
  if (band === 'YELLOW') return '#fde047';
  if (band === 'RED') return '#dc2626';
  return '#9ca3af';
};

// Analysis indicators with hover tooltip
function AnalysisIndicators({ analysis, modalRef, viewerName, candidateName, socialPlatforms }) {
  const [hoverData, setHoverData] = useState(null);
  const gridRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  // Calculate tooltip position when hover changes
  useEffect(() => {
    if (hoverData && gridRef.current && modalRef?.current) {
      const gridRect = gridRef.current.getBoundingClientRect();
      const modalRect = modalRef.current.getBoundingClientRect();

      // Position above the grid, centered in the modal
      setTooltipPos({
        bottom: window.innerHeight - gridRect.top + 8,
        left: modalRect.left + (modalRect.width / 2)
      });
    }
  }, [hoverData, modalRef]);

  if (!analysis) {
    return <span className="text-xs text-gray-400">Loading...</span>;
  }

  const getBorderColor = (status) => {
    if (status === 'GREEN') return 'border-green-300';
    if (status === 'YELLOW') return 'border-yellow-300';
    if (status === 'RED') return 'border-red-300';
    return 'border-gray-300';
  };

  // Build Service Area Overlap message — team avg radius vs candidate
  const buildServiceOverlapMessage = () => {
    const sao = analysis.serviceOverlap;
    if (!sao) return 'Service Area is loading...';

    const cFirst = (candidateName || 'Candidate').split(' ')[0];

    const overlapPct = Number(sao.overlap_percent);
    const teamRadius = Number(sao.viewer_radius_miles);
    const candidateRadius = Number(sao.candidate_radius_miles);

    const hasValidData =
      Number.isFinite(overlapPct) &&
      Number.isFinite(teamRadius) &&
      Number.isFinite(candidateRadius);

    if (!hasValidData) {
      return sao.message || 'Service Area is inconclusive (missing service radius or location).';
    }

    let teamOverlapPct = overlapPct;
    let candidateOverlapPct = overlapPct;

    const rSmall = Math.min(teamRadius, candidateRadius);
    const rLarge = Math.max(teamRadius, candidateRadius);
    const pctLarge = (overlapPct * (rSmall * rSmall) / (rLarge * rLarge));

    if (teamRadius <= candidateRadius) {
      teamOverlapPct = overlapPct;
      candidateOverlapPct = pctLarge;
    } else {
      teamOverlapPct = pctLarge;
      candidateOverlapPct = overlapPct;
    }
    teamOverlapPct = Math.max(0, Math.min(100, teamOverlapPct));
    candidateOverlapPct = Math.max(0, Math.min(100, candidateOverlapPct));

    const teamRadiusLabel = teamRadius % 1 === 0 ? teamRadius.toFixed(0) : teamRadius.toFixed(1);
    const candidateRadiusLabel = candidateRadius % 1 === 0 ? candidateRadius.toFixed(0) : candidateRadius.toFixed(1);

    let assessmentText = '';
    if (overlapPct < 25) {
      assessmentText = 'Weak overlap. Very little shared territory.';
    } else if (overlapPct < 50) {
      assessmentText = 'Acceptable overlap. Some shared territory, but limited coverage.';
    } else if (overlapPct < 75) {
      assessmentText = 'Good overlap. A large portion of the service areas overlap.';
    } else {
      if (teamRadius >= candidateRadius) {
        assessmentText = `Strong overlap. All of ${cFirst}'s service area is within the team's average coverage, but the team's reach extends beyond.`;
      } else {
        assessmentText = `Strong overlap. The team's average service area is within ${cFirst}'s coverage, but ${cFirst}'s reach extends beyond.`;
      }
    }

    return (
      <div className="space-y-1">
        <div>The <span style={{ color: '#00aef7', fontWeight: 600 }}>average team service radius</span> is {teamRadiusLabel} miles. <span style={{ color: '#b8860b', fontWeight: 600 }}>{cFirst}'s service radius</span> is {candidateRadiusLabel} miles.</div>
        <div>The overlap is {teamOverlapPct.toFixed(1)}% of the team's area and {candidateOverlapPct.toFixed(1)}% of {cFirst}'s area.</div>
        {assessmentText ? <div>{assessmentText}</div> : null}
      </div>
    );
  };

  // Social Channels: compute band from platforms prop
  const scPlatforms = socialPlatforms || [];
  const scCount = scPlatforms.length;
  const scBand = scCount > 0 ? 'GREEN' : 'GRAY';
  const candidateFirst = ((candidateName || 'Candidate').split(' ')[0] || 'Candidate').trim();
  const hasPlatform = (p) => scPlatforms.includes(p);

  const buildSocialMessage = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-center gap-3">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('facebook') ? '#1877F2' : '#9ca3af'}>
          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('twitter') ? '#000000' : '#9ca3af'}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('youtube') ? '#FF0000' : '#9ca3af'}>
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('linkedin') ? '#0A66C2' : '#9ca3af'}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('tiktok') ? '#000000' : '#9ca3af'}>
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={hasPlatform('instagram') ? '#E4405F' : '#9ca3af'}>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      </div>
      <div className="text-center text-[10px] text-gray-500">
        {scCount === 0
          ? candidateFirst + ' has not connected any social platforms'
          : candidateFirst + ' has connected ' + scCount + ' of 6 social platforms'}
      </div>
    </div>
  );

  // Icon definitions: each has a title, hover message builder, status, and SVG content
  const icons = [
    {
      key: 'customerFocus',
      title: 'Customer Focus',
      status: analysis.customerFocus?.status || 'GRAY',
      message: analysis.customerFocus?.message || '',
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="M200-120q-33 0-56.5-23.5T120-200v-160h80v160h160v80H200Zm400 0v-80h160v-160h80v160q0 33-23.5 56.5T760-120H600ZM120-600v-160q0-33 23.5-56.5T200-840h160v80H200v160h-80Zm640 0v-160H600v-80h160q33 0 56.5 23.5T840-760v160h-80ZM338.5-338.5Q280-397 280-480t58.5-141.5Q397-680 480-680t141.5 58.5Q680-563 680-480t-58.5 141.5Q563-280 480-280t-141.5-58.5ZM565-395q35-35 35-85t-35-85q-35-35-85-35t-85 35q-35 35-35 85t35 85q35 35 85 35t85-35Zm-85-85Z"/></svg>,
    },
    {
      key: 'serviceOverlap',
      title: 'Service Area',
      status: analysis.serviceOverlap?.status || 'GRAY',
      message: buildServiceOverlapMessage(),
      kind: 'SERVICE_OVERLAP',
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="M162-162q-82-82-82-198t82-198q82-82 198-82t198 82q82 82 82 198t-82 198q-82 82-198 82t-198-82Zm339.5-56.5Q560-277 560-360t-58.5-141.5Q443-560 360-560t-141.5 58.5Q160-443 160-360t58.5 141.5Q277-160 360-160t141.5-58.5ZM678-332q2-6 2-13v-15q0-133-93.5-226.5T360-680h-15q-7 0-13 2 26-88 98.5-145T600-880q116 0 198 82t82 198q0 97-57 169.5T678-332Z"/></svg>,
    },
    {
      key: 'professionExclusivity',
      title: 'Profession Exclusivity',
      status: analysis.professionExclusivity?.status || 'GRAY',
      message: analysis.professionExclusivity?.message || '',
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="M481-781q106 0 200 45.5T838-604q7 9 4.5 16t-8.5 12q-6 5-14 4.5t-14-8.5q-55-78-141.5-119.5T481-741q-97 0-182 41.5T158-580q-6 9-14 10t-14-4q-7-5-8.5-12.5T126-602q62-85 155.5-132T481-781Zm0 94q135 0 232 90t97 223q0 50-35.5 83.5T688-257q-51 0-87.5-33.5T564-374q0-33-24.5-55.5T481-452q-34 0-58.5 22.5T398-374q0 97 57.5 162T604-121q9 3 12 10t1 15q-2 7-8 12t-15 3q-104-26-170-103.5T358-374q0-50 36-84t87-34q51 0 87 34t36 84q0 33 25 55.5t59 22.5q34 0 58-22.5t24-55.5q0-116-85-195t-203-79q-118 0-203 79t-85 194q0 24 4.5 60t21.5 84q3 9-.5 16T208-205q-8 3-15.5-.5T182-217q-15-39-21.5-77.5T154-374q0-133 96.5-223T481-687Zm0-192q64 0 125 15.5T724-819q9 5 10.5 12t-1.5 14q-3 7-10 11t-17-1q-53-27-109.5-41.5T481-839q-58 0-114 13.5T260-783q-8 5-16 2.5T232-791q-4-8-2-14.5t10-11.5q56-30 117-46t124-16Zm0 289q93 0 160 62.5T708-374q0 9-5.5 14.5T688-354q-8 0-14-5.5t-6-14.5q0-75-55.5-125.5T481-550q-76 0-130.5 50.5T296-374q0 81 28 137.5T406-123q6 6 6 14t-6 14q-6 6-14 6t-14-6q-59-62-90.5-126.5T256-374q0-91 66-153.5T481-590Zm-1 196q9 0 14.5 6t5.5 14q0 75 54 123t126 48q6 0 17-1t23-3q9-2 15.5 2.5T744-191q2 8-3 14t-13 8q-18 5-31.5 5.5t-16.5.5q-89 0-154.5-60T460-374q0-8 5.5-14t14.5-6Z"/></svg>,
    },
    {
      key: 'reviewQuantity',
      title: 'Review Quantity',
      status: analysis.reviewQuantity?.status || 'GRAY',
      message: analysis.reviewQuantity?.message || '',
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="m363-390 117-71 117 71-31-133 104-90-137-11-53-126-53 126-137 11 104 90-31 133ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Zm-46 0v-480 480Z"/></svg>,
    },
    {
      key: 'scores',
      title: 'Score Summary',
      status: analysis.scores?.status || 'GRAY',
      message: analysis.scores?.message || '',
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="M240-400h122l200-200q9-9 13.5-20.5T580-643q0-11-5-21.5T562-684l-36-38q-9-9-20-13.5t-23-4.5q-11 0-22.5 4.5T440-722L240-522v122Zm280-243-37-37 37 37ZM300-460v-38l101-101 20 18 18 20-101 101h-38Zm121-121 18 20-38-38 20 18Zm26 181h273v-80H527l-80 80ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Zm-46 0v-480 480Z"/></svg>,
    },
    {
      key: 'customers',
      title: 'Customer Counts',
      status: analysis.customers?.status || 'GRAY',
      message: analysis.customers?.message || '',
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="m520-260 140-140q11-11 17.5-26t6.5-32q0-34-24-58t-58-24q-19 0-37.5 11T520-492q-30-28-47-38t-35-10q-34 0-58 24t-24 58q0 17 6.5 32t17.5 26l140 140Zm336-130L570-104q-12 12-27 18t-30 6q-15 0-30-6t-27-18L103-457q-11-11-17-25.5T80-513v-287q0-33 23.5-56.5T160-880h287q16 0 31 6.5t26 17.5l352 353q12 12 17.5 27t5.5 30q0 15-5.5 29.5T856-390ZM513-160l286-286-353-354H160v286l353 354ZM260-640q25 0 42.5-17.5T320-700q0-25-17.5-42.5T260-760q-25 0-42.5 17.5T200-700q0 25 17.5 42.5T260-640Zm220 160Z"/></svg>,
    },
    {
      key: 'socialChannels',
      title: 'Social Channels',
      status: scBand,
      message: buildSocialMessage(),
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="M600-120v-120H440v-400h-80v120H80v-320h280v120h240v-120h280v320H600v-120h-80v320h80v-120h280v320H600ZM160-760v160-160Zm520 400v160-160Zm0-400v160-160Zm0 160h120v-160H680v160Zm0 400h120v-160H680v160ZM160-600h120v-160H160v160Z"/></svg>,
    },
    {
      key: 'profileCompleteness',
      title: 'Profile Check',
      status: analysis.profileCompleteness?.status || 'GRAY',
      message: (() => {
        const pc = analysis.profileCompleteness;
        if (!pc) return '';
        const cFirst = pc.candidateFirst || (candidateName || 'Candidate').split(' ')[0];
        return (
          <div className="space-y-1">
            <div className="text-xs font-bold text-[#00aef7]">{cFirst} — Profile Check</div>

            <div className="flex items-center justify-between gap-3">
              <div>
                Profile photo:{' '}
                <span className={pc.hasProfilePic ? 'text-green-600' : 'text-red-600'}>
                  {pc.hasProfilePic ? 'Yes' : 'Missing'}
                </span>
              </div>
              {pc.profilePicUrl ? (
                <img src={pc.profilePicUrl} alt="Profile" className="w-5 h-5 rounded-full object-cover border border-gray-200" />
              ) : (
                <div className="w-5 h-5 rounded-full border border-gray-200 bg-gray-100" />
              )}
            </div>

            <div className="flex items-center justify-between gap-3">
              <div>
                Company logo:{' '}
                <span className={pc.hasLogo ? 'text-green-600' : 'text-red-600'}>
                  {pc.hasLogo ? 'Yes' : 'Missing'}
                </span>
              </div>
              {pc.logoUrl ? (
                <img src={pc.logoUrl} alt="Logo" className="w-5 h-5 rounded object-contain border border-gray-200 bg-white" />
              ) : (
                <div className="w-5 h-5 rounded border border-gray-200 bg-gray-100" />
              )}
            </div>

            <div>
              Address:{' '}
              <span className={pc.hasAddress ? 'text-gray-700' : 'text-red-600'}>
                {pc.addrLine || (pc.hasAddress ? 'Yes' : 'Missing')}
              </span>
            </div>

            <div>
              Phone:{' '}
              <span className={pc.hasPhone ? 'text-gray-700' : 'text-red-600'}>
                {pc.hasPhone ? pc.phoneDisplay : 'Missing'}
              </span>
            </div>

            <div>
              About Us:{' '}
              <span className={pc.aboutWords >= 30 ? 'text-green-600' : pc.aboutWords > 0 ? 'text-yellow-600' : 'text-red-600'}>
                {pc.aboutWords} words
              </span>
            </div>
          </div>
        );
      })(),
      svg: (fill) => <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill={fill}><path d="m424-312 282-282-56-56-226 226-114-114-56 56 170 170ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z"/></svg>,
    },
  ];

  return (
    <div className="relative">
      <div ref={gridRef} className="grid grid-cols-4 gap-3 justify-items-center">
        {icons.map(({ key, title, status, message, svg, kind }) => (
          <div
            key={key}
            onMouseEnter={() => setHoverData({ status, message, title, kind })}
            onMouseLeave={() => setHoverData(null)}
            className="w-7 h-7 flex items-center justify-center cursor-default transition"
          >
            {svg(getStatusFill(status))}
          </div>
        ))}
      </div>

      {/* Hover tooltip - rendered via portal, positioned above icons and centered in modal */}
      {hoverData && hoverData.message && ReactDOM.createPortal(
        <div
          className={`fixed z-[99999] rounded-md border bg-white px-3 py-2 shadow-lg w-[400px] ${getBorderColor(hoverData.status)}`}
          style={{
            bottom: tooltipPos.bottom,
            left: tooltipPos.left,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[#00aef7] mb-1">
                {hoverData.title}
              </div>
              <div className="text-xs text-gray-700 whitespace-pre-line">
                {hoverData.message}
              </div>
            </div>
            {hoverData.kind === 'SERVICE_OVERLAP' && (() => {
              const mapUrl = buildServiceOverlapMapUrl(analysis.serviceOverlap);
              if (!mapUrl) return null;
              return (
                <div className="shrink-0 w-[170px]">
                  <div className="h-[110px] w-full rounded-md border border-gray-200 bg-white overflow-hidden">
                    <img src={mapUrl} alt="Service area" className="w-full h-full object-cover" />
                  </div>
                </div>
              );
            })()}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function RecruitingModal() {
  const { recruitingOpen, closeRecruiting, openCandidate, openTeam } = useModalStore();
  const bringToFront = useModalStore((s) => s.bringToFront);
  const recruitingZ = useModalStore((s) => s.getZIndex('recruiting'));
  // Drive ChatCenter via matrixStore
  const openWithCandidate = useMatrixStore(s => s.openWithCandidate);
  const openChatCenter = useMatrixStore(s => s.open);               // NEW
  const setSelectedThread = useMessagingStore(s => s.setSelectedThread);
  const createLocalInviteThread = useMessagingStore(s => s.createLocalInviteThread);

  // Ref for modal panel (used for tooltip positioning)
  const modalPanelRef = useRef(null);

  const {
    teamId,
    setTeamId,
    loadTeamList,
    listId,
    items,
    loading,
    error
  } = useRecruitingStore();


  // Header metadata UI state (must be declared before any effects that read it)
  const [teamMeta, setTeamMeta] = useState({
    name: '',
    captainName: '',
    captainAvatar: '',
    captainUId: null,
  });

  const captainPresence = useMatrixStore(s =>
    teamMeta.captainUId ? s.getUserPresence(`@u${teamMeta.captainUId}:ehandshake.com`) : 'offline'
  );

  // Team rating stats (score, review count, member count)
  const [teamRating, setTeamRating] = useState({
    avg: null,
    count: 0,
    memberCount: 1,
  });

  // Candidate compatibility analysis results: { [candidate_u_id]: { customerFocus, serviceOverlap, ... } }
  const [candidateAnalysis, setCandidateAnalysis] = useState({});

  // Candidate social platforms map: { [candidate_u_id]: ['facebook', 'linkedin', ...] }
  const [candidateSocialPlatformsMap, setCandidateSocialPlatformsMap] = useState({});

  // Infinite scroll state
  const [visibleCount, setVisibleCount] = useState(8);
  const isFetchingMoreRef = useRef(false);

  // Reset visibleCount when items change (new team loaded, etc.)
  useEffect(() => {
    setVisibleCount(8);
  }, [items]);

  const onCandidatesScroll = (e) => {
    const el = e.currentTarget;
    if (!el) return;

    // near-bottom trigger
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (!nearBottom) return;
    if (isFetchingMoreRef.current) return;
    if (!Array.isArray(items) || visibleCount >= items.length) return;

    isFetchingMoreRef.current = true;

    setVisibleCount((prev) => {
      const next = prev + 8;
      return next > items.length ? items.length : next;
    });

    // small throttle so it doesn't fire repeatedly while pinned at bottom
    window.setTimeout(() => {
      isFetchingMoreRef.current = false;
    }, 150);
  };

// --- helper: resolve candidate meta from caches (no hooks here) ---
const resolveCandidateMeta = (uid) => {
  const result = {
    candidateId: Number(uid),
    candidateName: 'User',
    candidateAvatar: '/images/default-avatar.png',
    candidateBusiness: 'Business Name'
  };

  try {
    const st = useOtherUsersStore.getState?.();
    if (st) {
      const numericId = Number(uid);

      // 1) PRIMARY: same source as CandidateCell – entities.users[uid]
      const entitiesUsers =
        st.entities && st.entities.users ? st.entities.users : null;

      if (entitiesUsers && Number.isFinite(numericId)) {
        const u = entitiesUsers[numericId];
        if (u) {
          const name =
            (u.full_name ||
              [u.first_name, u.last_name].filter(Boolean).join(' ').trim()) ||
            result.candidateName;

          result.candidateName = name;

          // PHOTO-FIRST: profile picture, then any profileImg, then avatar, then default
          result.candidateAvatar =
            u.u_profile_picture ||
            u.profileImg ||
            u.avatar ||
            result.candidateAvatar;

          result.candidateBusiness =
            u.b_name ||
            u.business_name ||
            u.companyName ||
            result.candidateBusiness;
        }
      }

      // 2) SECONDARY: fall back to older shapes (byId / users / getById)
      if (
        result.candidateName === 'User' ||
        result.candidateAvatar === '/images/default-avatar.png'
      ) {
        const byId = st.byId || st.usersById || null;
        let user = null;

        if (byId && typeof byId === 'object') {
          user = byId[numericId] || byId[String(uid)] || null;
        }
        if (!user && Array.isArray(st.users)) {
          user = st.users.find((u) => Number(u.u_id) === numericId) || null;
        }
        if (!user && typeof st.getById === 'function') {
          user = st.getById(numericId) || null;
        }

        if (user) {
          const first = user.u_first_name || user.first_name || '';
          const last = user.u_last_name || user.last_name || '';
          const name =
            [first, last].filter(Boolean).join(' ').trim() ||
            result.candidateName;

          result.candidateName = name;
          result.candidateAvatar =
            user.u_profile_picture ||
            user.profileImg ||
            user.avatar ||
            result.candidateAvatar;
          result.candidateBusiness =
            user.b_name ||
            user.business_name ||
            user.companyName ||
            result.candidateBusiness;
        }
      }
    }

    // 3) FINAL FALLBACK: Team store pools if nothing else populated it
    if (
      result.candidateName === 'User' ||
      result.candidateAvatar === '/images/default-avatar.png'
    ) {
      const ts =
        typeof window !== 'undefined' &&
        window.__teamStore &&
        window.__teamStore.getState
          ? window.__teamStore.getState()
          : null;
      const pool = (ts && (ts.localUsers || ts.users)) || [];
      const lu = Array.isArray(pool)
        ? pool.find((u) => Number(u.u_id) === Number(uid))
        : null;
      if (lu) {
        const first = lu.u_first_name || lu.first_name || '';
        const last = lu.u_last_name || lu.last_name || '';
        const name =
          [first, last].filter(Boolean).join(' ').trim() ||
          result.candidateName;

        result.candidateName = name;
        result.candidateAvatar =
          lu.u_profile_picture ||
          lu.profileImg ||
          lu.avatar ||
          result.candidateAvatar;
        result.candidateBusiness =
          lu.b_name ||
          lu.business_name ||
          lu.companyName ||
          result.candidateBusiness;
      }
    }
  } catch (e) {
    // keep defaults
  }

  return result;
};




  // Prefetch candidate profiles (name/avatar/business/rating) into otherUsersStore whenever shortlist changes
  // This ensures users are hydrated regardless of which page the modal is opened from
  useEffect(() => {
    if (!recruitingOpen) return;

    const ids = (Array.isArray(items) ? items : [])
      .map(it => Number(it.candidate_u_id))
      .filter(n => Number.isFinite(n));
    if (ids.length === 0) return;

    // Clear cache timestamps to force fresh fetch (ensures all user data is loaded)
    const store = useOtherUsersStore.getState();
    for (const id of ids) {
      if (store.fetchedAt) {
        delete store.fetchedAt[id];
      }
    }

    // Batch fetch & cache - this calls /api/teams/users/by-ids which returns full user profiles
    store.fetchByIds(ids);
  }, [recruitingOpen, items]);

  // Fetch team rating summary when modal opens
  useEffect(() => {
    if (!recruitingOpen) return;
    const numericTeamId = Number(teamId);
    if (!Number.isFinite(numericTeamId) || numericTeamId <= 0) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `/api/teams/ratings/summary?team_id=${numericTeamId}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (cancelled) return;

        const avgRaw = data?.team_avg != null ? parseFloat(data.team_avg) : null;
        const avg = Number.isFinite(avgRaw) ? avgRaw : null;

        const countRaw = data?.team_review_count != null ? Number(data.team_review_count) : 0;
        const count = Number.isFinite(countRaw) ? countRaw : 0;

        const memberCountRaw = data?.member_count_total != null ? Number(data.member_count_total) : 0;
        const memberCount = Number.isFinite(memberCountRaw) ? memberCountRaw : 0;

        setTeamRating({
          avg: count > 0 ? avg : null,
          count,
          memberCount,
        });
      } catch (err) {
        console.error('[RecruitingModal] load team rating summary error:', err);
        if (!cancelled) {
          setTeamRating({ avg: null, count: 0, memberCount: 0 });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [recruitingOpen, teamId]);

  // Fetch candidate compatibility analysis + social platforms for all candidates when modal opens
  useEffect(() => {
    if (!recruitingOpen) return;
    const numericTeamId = Number(teamId);
    if (!Number.isFinite(numericTeamId) || numericTeamId <= 0) return;
    if (!Array.isArray(items) || items.length === 0) return;

    let cancelled = false;

    (async () => {
      const results = {};
      const socials = {};

      for (const item of items) {
        const candidateUId = Number(item?.candidate_u_id);
        if (!Number.isFinite(candidateUId)) continue;

        try {
          const [analysisRes, socialRes] = await Promise.all([
            fetch(
              `/api/analysis/team-candidate-compatibility?team_id=${numericTeamId}&candidate_u_id=${candidateUId}`,
              { credentials: 'include' }
            ),
            fetch(
              `/api/social/platforms/${candidateUId}`,
              { credentials: 'include' }
            ),
          ]);

          if (cancelled) return;

          if (analysisRes.ok) {
            const data = await analysisRes.json();
            if (data?.ok && data?.analysis) {
              results[candidateUId] = data.analysis;
            }
          }

          if (socialRes.ok) {
            const sData = await socialRes.json();
            if (sData?.ok) {
              socials[candidateUId] = sData.platforms || [];
            }
          }
        } catch (err) {
          console.error('[RecruitingModal] analysis fetch error for candidate', candidateUId, err);
        }
      }

      if (!cancelled) {
        setCandidateAnalysis(results);
        setCandidateSocialPlatformsMap(socials);
      }
    })();

    return () => { cancelled = true; };
  }, [recruitingOpen, teamId, items]);

  // Read teams from the persisted Team store (eh_team_v1)
  const { myTeams } = useTeamStore();

  // Drag & Drop (drop candidates into the shortlist)
  const [isDropping, setIsDropping] = useState(false);

  const DRAG_MIME = 'application/ehandshake-candidate';

  const handleDragOver = (e) => {
    // Allow drop
    if (e?.dataTransfer?.types?.includes?.(DRAG_MIME)) {
      e.preventDefault();
      setIsDropping(true);
    }
  };

  const handleDragLeave = () => setIsDropping(false);

  const handleDrop = async (e) => {
    e.preventDefault?.();
    setIsDropping(false);

    // Require a team to be selected
    if (!Number.isFinite(Number(teamId))) {
      // no team chosen → ignore the drop
      return;
    }

    try {
      const payload = e.dataTransfer.getData(DRAG_MIME);
      if (!payload) return;
      const data = JSON.parse(payload);
      const candidate_u_id = Number(data?.candidate_u_id);
      if (!Number.isFinite(candidate_u_id)) return;

      const viewer = Number(window.localStorage.getItem('u_id')) || null;
      if (!viewer) return;

      // ---- Instant UI: seed otherUsersStore from whatever we already have ----
      try {
        // 1) Prefer a full user object from the drag payload (if your DnD includes it)
        //    Accepts fields like u_first_name/u_last_name/u_profile_picture/b_* etc.
        if (data && (data.u_first_name || data.u_last_name || data.u_profile_picture || data.b_name)) {
          useOtherUsersStore.getState().upsertUsers([{
            // Normalize typical payload keys:
            u_id: candidate_u_id,
            u_first_name: data.u_first_name || data.first_name,
            u_last_name:  data.u_last_name  || data.last_name,
            u_profile_picture: data.u_profile_picture || data.profile_image_url || data.avatar,
            b_id: data.b_id,
            b_name: data.b_name || data.business_name || data.companyName,
            b_industry: data.b_industry || data.industry,
            rating_avg: data.rating_avg || data.customersurvey_avg || data.avg_rating,
            rating_count: data.rating_count || data.customersurvey_count || data.review_count
          }]);
        } else {
          // 2) Otherwise, grab it from the Team store's local users (the right-hand list source)
          const ts = typeof window !== 'undefined' && window.__teamStore && window.__teamStore.getState
            ? window.__teamStore.getState()
            : null;
          const pool = (ts && (ts.localUsers || ts.users)) || [];
          const lu = Array.isArray(pool)
            ? pool.find(u => Number(u.u_id) === candidate_u_id)
            : null;
          if (lu) {
            useOtherUsersStore.getState().upsertUsers([lu]);
          }
        }
      } catch {
        // non-fatal; fall through to normal fetch path
      }
      // -----------------------------------------------------------------------

      // POST add (server already dedupes with ON CONFLICT DO NOTHING)
      const res = await fetch('/api/teams/recruiting/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          team_id: Number(teamId),
          added_by_u_id: Number(viewer),
          candidate_u_ids: [candidate_u_id],
        }),
      });
      if (!res.ok) throw new Error(`POST /items ${res.status}`);

      // Refresh the list for this team
      await loadTeamList(Number(teamId));

    } catch (err) {
      console.error('[RecruitingModal] drop failed:', err);
    }
  };

  // Position the panel to align with Column 1 (left) and the top of the columns grid
  const [panelLeft, setPanelLeft] = useState(null);
  const [panelTop, setPanelTop] = useState(null);

  // Nudge controls — edit these numbers to fine-tune alignment
  // X: increase to move RIGHT; decrease to move LEFT
  // Y: increase to move DOWN;  decrease to move UP
  const PANEL_OFFSET_X = 50;  // try 280–320 based on your screenshot
  const PANEL_OFFSET_Y = 50;   // small nudge down from the columns’ top

    // ----- Drag-to-move state (applies a translate on top of the computed left/top) -----
  const [drag, setDrag] = useState({ dx: 0, dy: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = React.useRef({ x: 0, y: 0, dx: 0, dy: 0 });

  const onDragStart = (e) => {
    // left click only
    if (e.button !== 0) return;
    e.preventDefault();

    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      dx: drag.dx,
      dy: drag.dy,
    };
    setDragging(true);

    // attach listeners to the window so you can drag outside the header
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
  };

  const onDragMove = (e) => {
    const { x, y, dx, dy } = dragStartRef.current;
    const ndx = dx + (e.clientX - x);
    const ndy = dy + (e.clientY - y);
    setDrag({ dx: ndx, dy: ndy });
  };

  const onDragEnd = () => {
    setDragging(false);
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
  };

  useEffect(() => {
    if (!recruitingOpen) return;

    // Prefer an explicit id on your grid wrapper for pixel-perfect alignment:
    // <div id="team-columns">...</div>
    const anchorEl =
      document.getElementById('team-columns') ||
      document.querySelector('[data-team-columns]') ||
      document.querySelector('.teaminfo-grid') ||
      document.querySelector('[data-col-grid]');

    if (anchorEl) {
      // getBoundingClientRect is viewport-relative; our modal’s parent is fixed to the viewport,
      // so we should NOT add window.scrollX/Y here.
      const r = anchorEl.getBoundingClientRect();
      setPanelLeft(r.left);
      setPanelTop(r.top);
    } else {
      // No anchor found: we’ll still use the manual offsets from the viewport origin.
      setPanelLeft(null);
      setPanelTop(null);
    }
  }, [recruitingOpen]);



  // Load TEAM master list when opening (no hard-coded user id).
  // Also handles case where teamId is pre-set (e.g., from TeamModal)
  useEffect(() => {
    let cancelled = false;
    if (!recruitingOpen) return;

    const viewer = Number(window.localStorage.getItem('u_id')) || null;
    if (!viewer) {
      console.warn('[RecruitingModal] No u_id in localStorage; not loading team list.');
      return;
    }

    (async () => {
      try {
        // If teamId is already set (e.g., from TeamModal via setRecruitingTeamId),
        // load that team's list directly and hydrate users
        const presetTeamId = Number(teamId);
        if (Number.isFinite(presetTeamId) && presetTeamId > 0) {
          await loadTeamList(presetTeamId);

          // After loading, hydrate users from the recruiting list
          const store = useRecruitingStore.getState();
          const candidateIds = (Array.isArray(store.items) ? store.items : [])
            .map(it => Number(it.candidate_u_id))
            .filter(n => Number.isFinite(n));

          if (candidateIds.length > 0) {
            // Clear cache to force fresh fetch
            const otherStore = useOtherUsersStore.getState();
            for (const id of candidateIds) {
              if (otherStore.fetchedAt) {
                delete otherStore.fetchedAt[id];
              }
            }
            await otherStore.fetchByIds(candidateIds);
          }
          return;
        }

        // Otherwise, check if user has teams and auto-select if only one
        const res = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${viewer}`, { credentials: 'include' });
        if (!res.ok) return;
        const json = await res.json();
        const myTeams = Array.isArray(json?.teams) ? json.teams : [];
        if (cancelled) return;

        // If exactly one team, auto-select and load it.
        if (myTeams.length === 1) {
          const tid = Number(myTeams[0].team_id);
          setTeamId(tid);
          await loadTeamList(tid);

          // Hydrate users after loading
          const store = useRecruitingStore.getState();
          const candidateIds = (Array.isArray(store.items) ? store.items : [])
            .map(it => Number(it.candidate_u_id))
            .filter(n => Number.isFinite(n));

          if (candidateIds.length > 0) {
            const otherStore = useOtherUsersStore.getState();
            for (const id of candidateIds) {
              if (otherStore.fetchedAt) {
                delete otherStore.fetchedAt[id];
              }
            }
            await otherStore.fetchByIds(candidateIds);
          }
        }
        // If 0 or >1 teams: do nothing here; the selector can be added later.
      } catch {
        /* silently skip */
      }
    })();

    return () => { cancelled = true; };
  }, [recruitingOpen, teamId, setTeamId, loadTeamList]);

// Build header metadata - fetch captain profile from API
useEffect(() => {
  if (!recruitingOpen || !teamId) return;

  let cancelled = false;

  // 1) Pull the active team from the Team store
  let t = null;
  try {
    t = Array.isArray(myTeams)
      ? myTeams.find((x) => Number(x.team_id) === Number(teamId))
      : null;
  } catch {
    t = null;
  }

  const teamName = (t && (t.team_name || t.name)) || `Team #${teamId}`;

  // 2) Start with whatever captain info might already be on the team object
  let capFirst = (t && (t.owner_first_name || t.captain_first_name || t.u_first_name || t.first_name)) || '';
  let capLast = (t && (t.owner_last_name || t.captain_last_name || t.u_last_name || t.last_name)) || '';
  let captainName = [capFirst, capLast].filter(Boolean).join(' ');
  let captainAvatar = (t && (t.owner_profile_picture || t.captain_profile_picture || t.u_profile_picture || t.avatar_url)) || '';

  // 3) If we don't have captain data, FETCH it from the API
  const fetchCaptainProfile = async () => {
    try {
      // Get the owner_u_id from the team
      const ownerId = t?.owner_u_id;
      if (!ownerId) {
        console.warn('[RecruitingModal] No owner_u_id found for team:', teamId);
        return;
      }

      // Fetch captain profile
      const res = await fetch(`/api/profile/${ownerId}`, { credentials: 'include' });
      if (res.ok) {
        const captainData = await res.json();
        if (!cancelled) {
          setTeamMeta({
            name: teamName,
            captainName: [
              captainData.u_first_name || captainData.first_name,
              captainData.u_last_name || captainData.last_name
            ].filter(Boolean).join(' ').trim() || 'Captain',
            captainAvatar: captainData.u_profile_picture || captainData.profile_picture || '',
            captainUId: Number(ownerId) || null,
          });
        }
      } else {
        console.warn('[RecruitingModal] Failed to fetch captain profile:', res.status);
        // Set fallback with team name only
        if (!cancelled) {
          setTeamMeta({
            name: teamName,
            captainName: '',
            captainAvatar: '',
            captainUId: Number(t?.owner_u_id) || null,
          });

        }
      }
    } catch (error) {
      console.error('[RecruitingModal] Error fetching captain profile:', error);
      // Set fallback on error
      if (!cancelled) {
        setTeamMeta({
          name: teamName,
          captainName: '',
          captainAvatar: '',
          captainUId: Number(t?.owner_u_id) || null,
        });
      }
    }
  };

  // 4) If we already have captain data, use it immediately
  if (captainName && captainAvatar) {
    setTeamMeta({
      name: teamName,
      captainName,
      captainAvatar,
      captainUId: Number(t?.owner_u_id) || null,
    });
  } else {
    // Otherwise fetch from API
    fetchCaptainProfile();
  }

  return () => { cancelled = true; };
}, [recruitingOpen, teamId, myTeams]);

// Actively fetch Matrix presence for captain + all candidates via our Express API.
// The Synapse client-side getPresence() returns 403 for users who don't share a room,
// so we use our server-side endpoint that queries Synapse with the admin token.
useEffect(() => {
  if (!recruitingOpen || !items?.length) return;

  let cancelled = false;

  const uids = new Set();
  if (teamMeta.captainUId) uids.add(Number(teamMeta.captainUId));
  items.forEach((it) => {
    const cid = Number(it.candidate_u_id);
    if (Number.isFinite(cid) && cid > 0) uids.add(cid);
  });

  const fetchPresence = async () => {
    if (!uids.size) return;
    try {
      const res = await fetch(`/api/matrix/presence?uids=${[...uids].join(',')}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (cancelled || !data?.ok) return;
      const store = useMatrixStore.getState();
      for (const [uid, presence] of Object.entries(data.presence)) {
        const mxid = `@u${uid}:ehandshake.com`;
        store.setUserPresence(mxid, presence);
      }
    } catch (err) {
      console.warn('[RecruitingModal] presence fetch error:', err);
    }
  };

  fetchPresence();

  // Re-fetch every 30 seconds while the modal is open
  const interval = setInterval(fetchPresence, 30000);

  return () => {
    cancelled = true;
    clearInterval(interval);
  };
}, [recruitingOpen, items, teamMeta.captainUId]);


  if (!recruitingOpen) return null;


  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: recruitingZ }}
    >

      {/* Modal panel */}
      <div
        ref={modalPanelRef}
        onMouseDownCapture={() => bringToFront('recruiting')}
        className="absolute w-[420px] rounded-xl bg-white shadow-2xl h-[600px] overflow-hidden pointer-events-auto"
        style={{
          left:
            typeof panelLeft === 'number'
              ? `${panelLeft + PANEL_OFFSET_X}px`
              : `${PANEL_OFFSET_X}px`,
          top:
            typeof panelTop === 'number'
              ? `${panelTop + PANEL_OFFSET_Y}px`
              : `${PANEL_OFFSET_Y}px`,
          transform: `translate(${drag.dx}px, ${drag.dy}px)`
        }}

        role="dialog"
        aria-modal="true"
        aria-labelledby="recruiting-modal-title"
      >






        {/* Header */}
        <div
          className={`flex items-center justify-between px-3 py-0 bg-[#00aef7] text-white select-none ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseDown={onDragStart}
        >
          <h2 id="recruiting-modal-title" className="text-base font-semibold">
            Master Recruiting List
          </h2>
          <button
            type="button"
            onClick={closeRecruiting}
            className="rounded-full p-2 hover:bg-white/10 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>


        {/* Body */}
        <div className="px-6 py-0 overflow-visible">
          {/* Header layout: row 1 = centered team name; row 2 = two columns (left blank, right = captain) */}
          <div className="mb-4">
            {/* Row 1: Team Name (centered, clickable → opens TeamModal for this team) */}
            <div className="text-center">
              <button
                className="text-[#00aef7] text-lg font-semibold hover:underline cursor-pointer bg-transparent border-none p-0"
                onClick={() => { if (teamId) openTeam(teamId); }}
              >
                {teamMeta.name || (teamId ? `Team #${teamId}` : '')}
              </button>
            </div>

            {/* Row 2: two columns */}
            <div className="mt-2 grid grid-cols-2 items-start">
              {/* Left column: Team Stats */}
              <div className="flex flex-col items-center leading-tight">
                <span className="text-sm font-semibold text-gray-700">Team Score</span>
                {teamRating.avg != null ? (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-[#00aef7] leading-none">
                        {teamRating.avg.toFixed(2)}
                      </span>
                      <span className="text-sm text-gray-500 leading-none">
                        ({teamRating.count} {teamRating.count === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                    {/* Star rating */}
                    <div className="flex leading-none -mt-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span
                          key={i}
                          className={
                            i < Math.round(teamRating.avg)
                              ? 'text-yellow-400 text-xl'
                              : 'text-gray-300 text-xl'
                          }
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <span className="text-sm text-gray-500">No reviews yet</span>
                )}
                {/* Member count */}
                <div className="flex items-center gap-1 text-sm text-gray-600 leading-none">
                  <span>Members:</span>
                  <span className="font-bold text-base">{teamRating.memberCount}</span>
                </div>
              </div>

              {/* Right column: larger avatar, name, and role */}
              {teamMeta.captainName ? (
                <div className="flex flex-col items-center gap-0">
                  <div className="relative">
                    <img
                      src={teamMeta.captainAvatar || '/images/default-avatar.png'}
                      alt={teamMeta.captainName}
                      className={`w-[60px] h-[60px] rounded-full object-cover ${captainPresence === 'online' ? 'border-2 border-green-500' : captainPresence === 'unavailable' ? 'border-2 border-yellow-500' : 'border border-[#00aef7]/20'}`}
                      onError={(e) => { e.currentTarget.src = '/images/default-avatar.png'; }}
                    />

                  </div>
                  <div className="text-sm text-gray-800 text-center pb-0 max-w-[12rem] truncate">
                    {teamMeta.captainName}
                  </div>
                  <div className="text-xs text-gray-500">Team Captain</div>
                </div>
              ) : (
                <div />
              )}

            </div>
          </div>



          {/* Full shortlist */}
          {/* Shortlist section (renders even when empty so new teams see a drop zone) */}
          <div className="rounded-lg border px-0">
            <div className="flex items-center justify-between border-b px-2 py-1 text-sm">
              <span className="font-semibold text-[#00aef7]">Shortlisted Candidates</span>
              <span className="text-gray-600">
                {Array.isArray(items) ? items.length : 0} total
              </span>
            </div>

            {Array.isArray(items) && items.length > 0 ? (
              <ul
              className={`max-h-[380px] overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent px-4 py-2 pr-2 pb-20 text-sm transition ring-offset-2 ${isDropping ? 'ring-2 ring-[#00aef7] rounded-md' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onScroll={onCandidatesScroll}
              >

                {items.slice(0, visibleCount).map((it) => (
                  <li
                    key={it.recruiting_list_item_id}
                    className="relative flex flex-col gap-1 border-b py-2 last:border-b-0"
                  >
                    {/* Global tiny X in the top-right of the entire card */}
                    <button
                      type="button"
                      onClick={() =>
                        useRecruitingStore.getState().removeItem(it.recruiting_list_item_id)
                      }
                      className="absolute -top-1.5 right-2 text-[#00aef7] text-2xl font-extrabold leading-none hover:opacity-80"
                      title="Remove from Shortlist"
                      aria-label="Remove from Shortlist"
                    >
                      ×
                    </button>

                    <div className="grid grid-cols-[40%,60%] items-start">
                      {/* LEFT COLUMN: avatar + icons row, then name below */}
                      <div className="relative">
                        <CandidateCellWithIcons
                          uid={Number(it.candidate_u_id)}
                          item={it}
                          resolveCandidateMeta={resolveCandidateMeta}
                          createLocalInviteThread={createLocalInviteThread}
                          setSelectedThread={setSelectedThread}
                          openWithCandidate={openWithCandidate}
                          openChatCenter={openChatCenter}
                          openCandidate={openCandidate}
                        />
                      </div>
                      {/* RIGHT COLUMN: Analysis indicators */}
                      <div className="flex flex-col items-start gap-2 pt-1 -ml-5">
                        {/* Analysis indicators grid */}
                        <AnalysisIndicators
                          analysis={candidateAnalysis[Number(it.candidate_u_id)]}
                          modalRef={modalPanelRef}
                          viewerName={teamMeta.captainName}
                          candidateName={resolveCandidateMeta(it.candidate_u_id)?.candidateName}
                          socialPlatforms={candidateSocialPlatformsMap[Number(it.candidate_u_id)]}
                        />
                      </div>
                    </div>
                    {/* Full-width host for editor/preview */}
                    <div id={`notes-host-${it.recruiting_list_item_id}`} />
                  </li>

                ))}
              </ul>
            ) : (
              <div
                className={`px-4 py-6 text-sm text-gray-500 text-center transition ${
                  isDropping ? 'ring-2 ring-[#00aef7] rounded-md' : ''
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                No candidates yet. Drag profiles here to add them.
              </div>
            )}
          </div>


        </div>



      </div>
    </div>
  );
}




  // REPLACE the entire NotesMini component with this version
  function NotesMini({ item }) {
    const { updateItem, loading } = useRecruitingStore();

    // local mirror of what's stored so we can show it immediately after save
    const [persisted, setPersisted] = useState(item?.notes || '');
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [flash, setFlash] = useState(null); // just the newly added line, for 5s

    // keep our local mirror in sync if store rehydrates item.notes later
    useEffect(() => {
      setPersisted(item?.notes || '');
    }, [item?.notes]);

    // Button label toggles after first save
    const hasNotes = !!(persisted && String(persisted).trim());
    const btnLabel = hasNotes ? 'Modify Notes' : 'Add Note';

    // full-width host element for portal
    const hostId = `notes-host-${item.recruiting_list_item_id}`;
    const [hostEl, setHostEl] = useState(null);
    useEffect(() => {
      setHostEl(document.getElementById(hostId) || null);
    }, [hostId]);

    const formatDate = (d = new Date()) => {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = String(d.getFullYear());
      return `${mm}/${dd}/${yyyy}`; // e.g., 10/15/2025
    };

    const onCancel = () => {
      setDraft('');
      setOpen(false);
    };

    const onSave = async () => {
      const add = (draft || '').trim();
      if (!add) return;

      // Append-only with a date stamp at the END of the new line
      const line = `${add} — ${formatDate()}`;
      const existing = (persisted || '').trim();
      const combined = existing ? `${existing}\n${line}` : line;

      // Optimistic local update so it's "sticky" immediately
      setPersisted(combined);

      // Send to backend/store
      try {
        await updateItem(item.recruiting_list_item_id, { notes: combined });
      } catch {
        // If the request fails, we could revert, but keeping it is usually friendlier.
        // Optionally: show a toast.
      }

      // brief confirmation of just-added line
      setFlash(line);
      setDraft('');
      setOpen(false);

      // hide confirmation after 5 seconds
      setTimeout(() => setFlash(null), 5000);
    };

    // Full-width content (portal target)
    const body = hostEl ? (
      <div className="px-2">
        {/* Show previous notes when editing, read-only */}
        {open && hasNotes && (
          <div className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[12.5px] text-gray-700 whitespace-pre-wrap">
            {persisted}
          </div>
        )}

        {open && (
          <div className="mb-2">
            <textarea
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Private notes (add only — existing text cannot be edited or deleted)"
              className="w-full min-h-[56px] rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#00aef7]"
            />
            <div className="mt-1 flex items-center gap-2">
              {/* Save: small green check */}
              <button
                type="button"
                onClick={onSave}
                disabled={loading}
                title="Save"
                aria-label="Save"
                className="p-1 hover:opacity-90 disabled:opacity-60"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </button>

              {/* Cancel: small red X */}
              <button
                type="button"
                onClick={onCancel}
                title="Cancel"
                aria-label="Cancel"
                className="p-1 hover:opacity-90"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Show the just-added line for 5s after save, then collapse */}
        {flash && !open && (
          <div className="rounded-md border border-[#00aef7]/30 bg-[#00aef7]/5 px-3 py-2 text-[12.5px] text-gray-700 whitespace-pre-wrap">
            {flash}
          </div>
        )}
      </div>
    ) : null;

    return (
      <>
        {/* Icon trigger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-2 rounded-full text-[#00aef7] hover:bg-[#00aef7]/10 transition"
          title={btnLabel}
          aria-label={btnLabel}
        >
          <RiStickyNoteAddFill size={20} />
        </button>

        {/* Portal the full-width editor/flash into the card host */}
        {hostEl && body ? ReactDOM.createPortal(body, hostEl) : null}
      </>
    );
  }



  function CandidateCell({ uid }) {
    // Subscribe reactively to just this user's record
    const user = useOtherUsersStore(
      (s) => s.entities.users && s.entities.users[Number(uid)]
    );
    const presence = useMatrixStore(s =>
      s.getUserPresence(`@u${Number(uid)}:ehandshake.com`)
    );

    const name =
      (user?.full_name ||
        [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()) ||
      (Number.isFinite(Number(uid)) ? `User #${uid}` : 'User');

    const avatar = user?.avatar || '/images/default-avatar.png';

    return (
      <div className="flex flex-col items-start w-[140px]">
        <div className="relative">
          <img
            src={avatar}
            alt={name}
            className={`w-[45px] h-[45px] rounded-full object-cover ${getPresenceBorder(presence)}`}
            onError={(e) => {
              e.currentTarget.src = '/images/default-avatar.png';
            }}
          />
        </div>
        <div className="mt-1 text-sm text-gray-800 text-left leading-snug break-words whitespace-normal px-2">
          {name}
        </div>
      </div>
    );
  }

  // CandidateCellWithIcons: avatar + icons side by side, name below
  function CandidateCellWithIcons({ uid, item, resolveCandidateMeta, createLocalInviteThread, setSelectedThread, openWithCandidate, openChatCenter, openCandidate }) {
    // Get user entity (now includes b_business_industry directly)
    const user = useOtherUsersStore(
      (s) => s.entities.users?.[Number(uid)] || null
    );
    const presence = useMatrixStore(s =>
      s.getUserPresence(`@u${Number(uid)}:ehandshake.com`)
    );

    const name =
      (user?.full_name ||
        [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()) ||
      (Number.isFinite(Number(uid)) ? `User #${uid}` : 'User');

    const avatar = user?.avatar || '/images/default-avatar.png';
    const businessIndustry = user?.b_business_industry || '';

    const handleAvatarClick = async () => {
      const viewerUId = Number(window.localStorage.getItem('u_id'));
      if (!Number.isFinite(viewerUId) || !Number.isFinite(Number(uid))) return;

      // Force a fresh fetch to ensure we have all the latest fields
      // Clear the cached timestamp to force refetch
      const store = useOtherUsersStore.getState();
      if (store.fetchedAt) {
        delete store.fetchedAt[uid];
      }
      await store.fetchByIds([uid]);

      // Get hydrated user data from the store
      const hydratedUser = useOtherUsersStore.getState().getUser(uid);

      // Map to the field names CandidateListModal expects
      const candidateData = hydratedUser ? {
        u_id: hydratedUser.u_id,
        u_first_name: hydratedUser.first_name,
        u_last_name: hydratedUser.last_name,
        u_profile_picture: hydratedUser.avatar,
        u_email: hydratedUser.u_email,
        u_job_title: hydratedUser.u_job_title,
        u_phone_number: hydratedUser.u_phone_number,
        u_url_name: hydratedUser.u_url_name,
        u_avg_rating: hydratedUser.u_avg_rating,
        u_review_count: hydratedUser.u_review_count,
        u_customer_focus: hydratedUser.u_customer_focus,
        b_id: hydratedUser.b_id,
        b_name: hydratedUser.b_name,
        b_industry: hydratedUser.b_industry,
        b_business_industry: hydratedUser.b_business_industry,
        b_phone: hydratedUser.b_phone,
        b_website: hydratedUser.b_website,
        b_logo: hydratedUser.b_logo,
        b_address: hydratedUser.b_address,
        b_city: hydratedUser.b_city,
        b_state: hydratedUser.b_state,
        b_zip_code: hydratedUser.b_zip_code,
        b_about_us: hydratedUser.b_about_us,
      } : { u_id: uid };

      openCandidate({
        candidate: candidateData,
        viewerUId,
      });
    };

    return (
      <div className="flex flex-col items-start w-[140px]">
        {/* Row 1: Avatar + Icons side by side */}
        <div className="flex items-center gap-1">
          <div className="relative">
            <img
              src={avatar}
              alt={name}
              className={`w-[45px] h-[45px] rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-[#00aef7] transition ${getPresenceBorder(presence)}`}
              onClick={handleAvatarClick}
              title="View profile"
              onError={(e) => {
                e.currentTarget.src = '/images/default-avatar.png';
              }}
            />
          </div>
          {/* Icons: Notes + Message */}
          <div className="flex flex-col gap-0">
            <NotesMini item={item} />
            <button
              onClick={() => {
                const myUid = Number(window.localStorage.getItem('u_id'));
                const candidateId = Number(item?.candidate_u_id);

                if (!Number.isFinite(myUid) || !Number.isFinite(candidateId)) {
                  console.error('[RecruitingModal] Invalid user IDs for messaging:', { myUid, candidateId });
                  return;
                }

                const meta = resolveCandidateMeta(candidateId);

                try {
                  createLocalInviteThread({
                    candidateId: meta.candidateId,
                    candidateName: meta.candidateName,
                    candidateAvatar: meta.candidateAvatar,
                    candidateBusiness: meta.candidateBusiness,
                  });
                } catch (e) {
                  console.error('[RecruitingModal] createLocalInviteThread failed', e);
                }

                const a = Math.min(myUid, candidateId);
                const b = Math.max(myUid, candidateId);
                const aliasFull = `#eh.dm.u${a}_u${b}:ehandshake.com`;
                setSelectedThread(aliasFull);

                openWithCandidate({
                  candidateId: meta.candidateId,
                  candidateName: meta.candidateName,
                  candidateAvatar: meta.candidateAvatar,
                  candidateBusiness: meta.candidateBusiness
                });

                openChatCenter();
              }}
              className={`p-1 rounded-full transition ${presence === 'online' ? 'text-green-500 hover:bg-green-500/10' : 'text-[#00aef7] hover:bg-[#00aef7]/10'}`}
              title="Message"
              aria-label="Message"
            >
              <svg viewBox="0 0 576 512" height="18" width="18" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                <path d="M416 192c0-88.4-93.1-160-208-160S0 103.6 0 192c0 34.3 14.1 65.9 38 92-13.4 30.2-35.5 54.2-35.8 54.5-2.2 2.3-2.8 5.7-1.5 8.7S4.8 352 8 352c36.6 0 66.9-12.3 88.7-25 32.2 15.7 70.3 25 111.3 25 114.9 0 208-71.6 208-160zm122 220c23.9-26 38-57.7 38-92 0-66.9-53.5-124.2-129.3-148.1.9 6.6 1.3 13.3 1.3 20.1 0 105.9-107.7 192-240 192-10.8 0-21.3-.8-31.7-1.9C207.8 439.6 281.8 480 368 480c41 0 79.1-9.2 111.3-25 21.8 12.7 52.1 25 88.7 25 3.2 0 6.1-1.9 7.3-4.8 1.3-2.9.7-6.3-1.5-8.7-.3-.3-22.4-24.2-35.8-54.5z"></path>
              </svg>
            </button>
          </div>
        </div>
        {/* Row 2: Name below */}
        <div className="mt-1 text-sm text-gray-800 text-left leading-snug break-words whitespace-normal">
          {name}
        </div>
        {/* Row 3: Business industry below name */}
        {businessIndustry && (
          <div className="text-xs text-gray-500 text-left leading-snug break-words whitespace-normal">
            {businessIndustry}
          </div>
        )}
      </div>
    );
  }

